import { useMemo } from "react";

import { computeAgeService } from "~/services/computeAgeService";

import type { Evaluation } from "../evalutation/types";

export function useCorporalEvaluation(evaluation?: Evaluation) {
	const percentualDeGorduraMedido = useMemo(() => {
		if (!evaluation?.corporalEvaluationProtocol) return 0;
		switch (evaluation.corporalEvaluationProtocol) {
			case "Direto":
				return evaluation.direct;
			case "Faulkner": {
				const ab = evaluation.abdominal.reduce((p, c) => p + c, 0) / 3;
				const tr = evaluation.triceptal.reduce((p, c) => p + c, 0) / 3;
				const si = evaluation.suprailiac.reduce((p, c) => p + c, 0) / 3;
				const se = evaluation.subscapular.reduce((p, c) => p + c, 0) / 3;

				return (tr + se + si + ab) * 0.153 + 5.783;
			}
			case "Perímetros Coté e Wilmore Masculino": {
				if (!evaluation) return 0;
				const PC = evaluation.currentWeight;
				const CA = evaluation.abdomen;
				const CP = evaluation.rightFist;
				if (!PC || !CA || !CP) return 0;
				const MCM = 41.955 + 1.038786 * PC - 0.82816 * (CA - CP);
				return ((PC - MCM) / PC) * 100;
			}
			case "Perímetros Penroe, Nelson e Fisher (1985) Feminino": {
				if (!evaluation) return 0;
				const A = evaluation.stature;
				const CQ = evaluation.hip;
				const CA = evaluation.abdomen;
				return 0.55 * CQ - 0.24 * A + 0.28 * CA - 8.43;
			}
			case "Pollock & Jackson 3 Dobras": {
				if (!evaluation?.student) return 0;
				const age = computeAgeService(evaluation.student.birthday);
				let DENS = 0;
				const PE =
					(evaluation.chest[0] + evaluation.chest[1] + evaluation.chest[2]) / 3;
				const TR =
					(evaluation.triceptal[0] +
						evaluation.triceptal[1] +
						evaluation.triceptal[2]) /
					3;
				const AB =
					(evaluation.abdominal[0] +
						evaluation.abdominal[1] +
						evaluation.abdominal[2]) /
					3;
				const CX =
					(evaluation.medialThigh[0] +
						evaluation.medialThigh[1] +
						evaluation.medialThigh[2]) /
					3;
				const SI =
					(evaluation.suprailiac[0] +
						evaluation.suprailiac[1] +
						evaluation.suprailiac[2]) /
					3;

				if (evaluation?.student.sex === "masculino") {
					DENS =
						1.10938 -
						0.0008267 * (PE + AB + CX) +
						0.0000016 * (PE + AB + CX) ** 2 -
						0.0002574 * age;
				} else {
					DENS =
						1.0994921 -
						0.0009929 * (TR + CX + SI) +
						0.0000023 * (TR + CX + SI) ** 2 -
						0.0001392 * age;
				}

				return (4.95 / DENS - 4.5) * 100;
			}
			case "Pollock & Jackson 7 Dobras": {
				if (!evaluation?.student) return 0;
				const age = computeAgeService(evaluation.student.birthday);
				let dens = 0;
				const se = evaluation.subscapular.reduce((p, c) => c + p, 0) / 3;
				const am = evaluation.axilla.reduce((p, c) => p + c, 0) / 3;
				const ti = evaluation.triceptal.reduce((p, c) => p + c, 0) / 3;
				const cm = evaluation.medialThigh.reduce((p, c) => p + c, 0) / 3;
				const si = evaluation.suprailiac.reduce((p, c) => p + c, 0) / 3;
				const ab = evaluation.abdominal.reduce((p, c) => p + c, 0) / 3;
				const pe = evaluation.chest.reduce((p, c) => p + c, 0) / 3;
				const sumdobras = se + am + ti + cm + si + ab + pe;
				if (evaluation.student.sex === "masculino") {
					dens =
						1.112 -
						0.00043499 * sumdobras +
						0.00000055 * sumdobras ** 2 -
						0.00028826 * age;
				} else {
					dens =
						1.097 -
						0.00046971 * sumdobras +
						0.00000056 * sumdobras ** 2 -
						0.00012828 * age;
				}
				return (4.95 / dens - 4.5) * 100;
			}

			case "Wetman e col., para pessoas obesas": {
				if (!evaluation) return 0;
				const P = evaluation.currentWeight;
				const A = evaluation.stature;
				const abdomen = evaluation.abdomen;
				if (evaluation?.student.sex === "masculino") {
					return 0.31457 * abdomen - 0.10969 * P + 10.8336;
				} else {
					return 0.11077 * abdomen - 0.17666 * A + 0.14354 * P + 51.03301;
				}
			}
			default:
				return 0;
		}
	}, [evaluation]);

	const pesoGordo = useMemo(() => {
		if (!evaluation) return 0;
		return +(
			(evaluation?.currentWeight * percentualDeGorduraMedido) /
			100
		).toFixed(1);
	}, [percentualDeGorduraMedido, evaluation]);

	const sugerePercentGordura = useMemo(() => {
		if (!evaluation?.student.birthday) return 0;
		const age = computeAgeService(evaluation.student.birthday);
		if (evaluation.student.sex === "masculino") {
			if (age <= 29) {
				if (percentualDeGorduraMedido <= 14.1) return percentualDeGorduraMedido;
				if (percentualDeGorduraMedido <= 17.4) return 13.0;
				if (percentualDeGorduraMedido <= 29.1) return 15.0;
				return 22.0;
			}
			if (age <= 39) {
				if (percentualDeGorduraMedido <= 17.5) return percentualDeGorduraMedido;
				if (percentualDeGorduraMedido <= 24.2) return 15.0;
				if (percentualDeGorduraMedido <= 29.9) return 18.0;
				return 21.0;
			}
			if (age <= 49) {
				if (percentualDeGorduraMedido <= 19.6) return percentualDeGorduraMedido;
				if (percentualDeGorduraMedido <= 22.5) return 17.0;
				if (percentualDeGorduraMedido <= 31.5) return 20.0;
				return 23.0;
			}
			if (age <= 59) {
				if (percentualDeGorduraMedido <= 21.3) return percentualDeGorduraMedido;
				if (percentualDeGorduraMedido <= 24.1) return 19;
				if (percentualDeGorduraMedido <= 32.4) return 22;
				return 25;
			}
			if (percentualDeGorduraMedido <= 22.0) return percentualDeGorduraMedido;
			if (percentualDeGorduraMedido <= 25.0) return 19;
			if (percentualDeGorduraMedido <= 33.4) return 22;
			return 25;
		} else {
			if (age <= 29) {
				if (percentualDeGorduraMedido <= 20.6) return percentualDeGorduraMedido;
				if (percentualDeGorduraMedido <= 23.7) return 18;
				if (percentualDeGorduraMedido <= 35.4) return 21;
				return 24;
			}
			if (age <= 39) {
				if (percentualDeGorduraMedido <= 21.6) return percentualDeGorduraMedido;
				if (percentualDeGorduraMedido <= 24.9) return 19;
				if (percentualDeGorduraMedido <= 35.7) return 22;
				return 25;
			}
			if (age <= 49) {
				if (percentualDeGorduraMedido <= 24.9) return percentualDeGorduraMedido;
				if (percentualDeGorduraMedido <= 28.1) return 22;
				if (percentualDeGorduraMedido <= 37.8) return 25;
				return 29;
			}
			if (age <= 59) {
				if (percentualDeGorduraMedido <= 28.5) return percentualDeGorduraMedido;
				if (percentualDeGorduraMedido <= 32.6) return 27;
				if (percentualDeGorduraMedido <= 39.6) return 29;
				return 33;
			}
			if (percentualDeGorduraMedido <= 29.3) return percentualDeGorduraMedido;
			if (percentualDeGorduraMedido <= 32.7) return 28;
			if (percentualDeGorduraMedido <= 40.5) return 30;
			return 33;
		}
	}, [evaluation, percentualDeGorduraMedido]);

	const MCMPercent = useMemo(() => {
		if (!percentualDeGorduraMedido) return 0;
		return +(100 - percentualDeGorduraMedido).toFixed(1);
	}, [percentualDeGorduraMedido]);

	const MCM = useMemo(() => {
		if (!evaluation?.currentWeight) return 0;

		return +(evaluation?.currentWeight - pesoGordo).toFixed(1);
	}, [pesoGordo, evaluation]);

	const MCMCarencia = useMemo(() => {
		if (!evaluation) return 0;
		const result =
			evaluation.student.sex === "masculino"
				? ((81 - MCMPercent) / 100) * MCM
				: ((77 - MCMPercent) / 100) * MCM;

		return +(
			(result < 0 ? 0 : result) + (evaluation?.weightAdjustment || 0)
		).toFixed(1);
	}, [MCM, MCMPercent, evaluation]);

	const MCMSugerido = useMemo(() => {
		return +(MCM + MCMCarencia).toFixed(1);
	}, [MCM, MCMCarencia]);

	const pesoGordoExcedenteDoProposto = useMemo(() => {
		if (!evaluation?.currentWeight) return 0;
		return +(
			((percentualDeGorduraMedido - (evaluation?.proposedFatPercentage || 0)) *
				evaluation?.currentWeight) /
			100
		).toFixed(1);
	}, [evaluation, percentualDeGorduraMedido]);

	const MCMPercentSugerido = useMemo(() => {
		if (!evaluation?.proposedFatPercentage) return 0;
		return +(100 - evaluation?.proposedFatPercentage).toFixed(1);
	}, [evaluation]);

	const CalcPesoSugerido = useMemo(() => {
		if (!percentualDeGorduraMedido) return 0;
		if (MCMCarencia < 0) {
			return MCM / (1 - (evaluation?.proposedFatPercentage || 0) * 0.01);
		} else {
			return +(
				(MCM + MCMCarencia) /
				(1 - (evaluation?.proposedFatPercentage || 0) * 0.01)
			).toFixed(1);
		}
	}, [
		MCM,
		MCMCarencia,
		evaluation?.proposedFatPercentage,
		percentualDeGorduraMedido,
	]);

	const classificacaoPercentGorduraIndex = useMemo((): number => {
		if (!evaluation) return 0;
		const age = computeAgeService(evaluation.student.birthday);
		if (evaluation.student.sex === "masculino") {
			if (age <= 29) {
				if (percentualDeGorduraMedido <= 9.4) return 1;
				if (percentualDeGorduraMedido <= 14.1) return 2;
				if (percentualDeGorduraMedido <= 17.4) return 3;
				if (percentualDeGorduraMedido <= 22.4) return 4;
				if (percentualDeGorduraMedido <= 29.1) return 5;
				return 6;
			}
			if (age <= 39) {
				if (percentualDeGorduraMedido <= 13.9) return 1;
				if (percentualDeGorduraMedido <= 17.5) return 2;
				if (percentualDeGorduraMedido <= 20.5) return 3;
				if (percentualDeGorduraMedido <= 24.2) return 4;
				if (percentualDeGorduraMedido <= 29.9) return 5;
				return 6;
			}
			if (age <= 49) {
				if (percentualDeGorduraMedido <= 16.3) return 1;
				if (percentualDeGorduraMedido <= 19.6) return 2;
				if (percentualDeGorduraMedido <= 22.5) return 3;
				if (percentualDeGorduraMedido <= 26.1) return 4;
				if (percentualDeGorduraMedido <= 31.5) return 5;
				return 6;
			}
			if (age <= 59) {
				if (percentualDeGorduraMedido <= 17.9) return 1;
				if (percentualDeGorduraMedido <= 21.3) return 2;
				if (percentualDeGorduraMedido <= 24.1) return 3;
				if (percentualDeGorduraMedido <= 27.5) return 4;
				if (percentualDeGorduraMedido <= 32.4) return 5;
				return 6;
			}
			if (percentualDeGorduraMedido <= 18.4) return 1;
			if (percentualDeGorduraMedido <= 22.0) return 2;
			if (percentualDeGorduraMedido <= 25.0) return 3;
			if (percentualDeGorduraMedido <= 28.5) return 4;
			if (percentualDeGorduraMedido <= 33.4) return 5;
			return 6;
		} else {
			if (age <= 29) {
				if (percentualDeGorduraMedido <= 17.1) return 1;
				if (percentualDeGorduraMedido <= 20.6) return 2;
				if (percentualDeGorduraMedido <= 23.7) return 3;
				if (percentualDeGorduraMedido <= 27.7) return 4;
				if (percentualDeGorduraMedido <= 35.4) return 5;
				return 6;
			}
			if (age <= 39) {
				if (percentualDeGorduraMedido <= 18.0) return 1;
				if (percentualDeGorduraMedido <= 21.6) return 2;
				if (percentualDeGorduraMedido <= 24.9) return 3;
				if (percentualDeGorduraMedido <= 29.3) return 4;
				if (percentualDeGorduraMedido <= 35.7) return 5;
				return 6;
			}
			if (age <= 49) {
				if (percentualDeGorduraMedido <= 21.3) return 1;
				if (percentualDeGorduraMedido <= 24.9) return 2;
				if (percentualDeGorduraMedido <= 28.1) return 3;
				if (percentualDeGorduraMedido <= 32.1) return 4;
				if (percentualDeGorduraMedido <= 37.8) return 5;
				return 6;
			}
			if (age <= 59) {
				if (percentualDeGorduraMedido <= 25.0) return 1;
				if (percentualDeGorduraMedido <= 28.5) return 2;
				if (percentualDeGorduraMedido <= 32.6) return 3;
				if (percentualDeGorduraMedido <= 35.6) return 4;
				if (percentualDeGorduraMedido <= 39.6) return 5;
				return 6;
			}
			if (percentualDeGorduraMedido <= 25.1) return 1;
			if (percentualDeGorduraMedido <= 29.3) return 2;
			if (percentualDeGorduraMedido <= 32.7) return 3;
			if (percentualDeGorduraMedido <= 36.6) return 4;
			if (percentualDeGorduraMedido <= 40.5) return 5;
			return 6;
		}
	}, [evaluation, percentualDeGorduraMedido]);

	const classificacaoPercentualGorduraEMCM = useMemo(() => {
		switch (classificacaoPercentGorduraIndex) {
			case 1:
				return "superior";
			case 2:
				return "excelente";
			case 3:
				return "bom";
			case 4:
				return "razoável";
			case 5:
				return "gordo";
			case 6:
				return "gordo.";
			default:
				return "inválido";
		}
	}, [classificacaoPercentGorduraIndex]);

	const pesoGordoSugerido = useMemo((): number => {
		return +(
			(CalcPesoSugerido * (evaluation?.proposedFatPercentage || 0)) /
			100
		).toFixed(1);
	}, [CalcPesoSugerido, evaluation?.proposedFatPercentage]);

	//   procedure TfrmAvaliacaoGPS.porcgordgrid;
	const linhaNaTabelaPercentualdeGordura = useMemo(() => {
		const cells: string[][] = [];
		if (!evaluation) return cells;

		cells[0] = ["", "Superior"];
		cells[1] = ["", "Excelente"];
		cells[2] = ["", "Bom"];
		cells[3] = ["", "Razoável"];
		cells[4] = ["", "Gordo"];
		cells[5] = ["", "Gordo."];

		const age = computeAgeService(evaluation?.student.birthday);

		if (evaluation?.student.sex === "masculino") {
			if (age <= 29) {
				cells[0][0] = "< 9,4%";
				cells[1][0] = "9,4 - 14,1%";
				cells[2][0] = "14,2 - 17,4%";
				cells[3][0] = "17,5 - 22,4%";
				cells[4][0] = "22,5 - 29,1%";
				cells[5][0] = "> 29,1%";
				return cells;
			}
			if (age <= 39) {
				cells[0][0] = "< 13,9%";
				cells[1][0] = "13,9 - 17,5%";
				cells[2][0] = "17,6 - 20,5%";
				cells[3][0] = "20,6 - 24,2%";
				cells[4][0] = "24,3 - 29,9%";
				cells[5][0] = "> 29,9%";
				return cells;
			}
			if (age <= 49) {
				cells[0][0] = "< 16,3%";
				cells[1][0] = "16,3 - 19,6%";
				cells[2][0] = "19,7 - 22,5%";
				cells[3][0] = "22,6 - 26,1%";
				cells[4][0] = "26,2 - 31,5%";
				cells[5][0] = "> 31,5%";
				return cells;
			}
			if (age <= 59) {
				cells[0][0] = "< 17,9%";
				cells[1][0] = "17,9 - 21,3%";
				cells[2][0] = "21,4 - 24,1%";
				cells[3][0] = "24,2 - 27,5%";
				cells[4][0] = "27,6 - 32,4%";
				cells[5][0] = "> 32,4%";
				return cells;
			}

			cells[0][0] = "< 18,4%";
			cells[1][0] = "18,4 - 22,0%";
			cells[2][0] = "22,1 - 25,0%";
			cells[3][0] = "25,1 - 28,5%";
			cells[4][0] = "28,6 - 33,4%";
			cells[5][0] = "> 33,4%";
			return cells;
		} else {
			if (age <= 29) {
				cells[0][0] = "< 17,1%";
				cells[1][0] = "17,1 - 20,6%";
				cells[2][0] = "20,7 - 23,7%";
				cells[3][0] = "23,8 - 27,7%";
				cells[4][0] = "27,8 - 35,4%";
				cells[5][0] = "> 35,4%";
				return cells;
			}
			if (age <= 39) {
				cells[0][0] = "< 18,0%";
				cells[1][0] = "18,0 - 21,6%";
				cells[2][0] = "21,7 - 24,9%";
				cells[3][0] = "25,0 - 29,3%";
				cells[4][0] = "29,4 - 35,7%";
				cells[5][0] = "> 35,7%";
				return cells;
			}
			if (age <= 49) {
				cells[0][0] = "< 21,3%";
				cells[1][0] = "21,3 - 24,9%";
				cells[2][0] = "25,0 - 28,1%";
				cells[3][0] = "28,2 - 32,1%";
				cells[4][0] = "32,2 - 37,8%";
				cells[5][0] = "> 37,8%";
				return cells;
			}
			if (age <= 59) {
				cells[0][0] = "< 25,0%";
				cells[1][0] = "25,0 - 28,5%";
				cells[2][0] = "28,6 - 32,6%";
				cells[3][0] = "32,7 - 35,6%";
				cells[4][0] = "35,7 - 39,6%";
				cells[5][0] = "> 39,6%";
				return cells;
			}

			cells[0][0] = "< 25,1%";
			cells[1][0] = "25,1 - 29,3%";
			cells[2][0] = "29,4 - 32,7%";
			cells[3][0] = "32,8 - 36,6%";
			cells[4][0] = "36,7 - 40,5%";
			cells[5][0] = "> 40,5%";
			return cells;
		}
	}, [evaluation]);

	return {
		pesoGordo,
		sugerePercentGordura,
		MCM,
		MCMPercent,
		MCMCarencia,
		MCMSugerido,
		pesoGordoExcedenteDoProposto,
		MCMPercentSugerido,
		CalcPesoSugerido,
		classificacaoPercentGorduraIndex,
		classificacaoPercentualGorduraEMCM,
		pesoGordoSugerido,
		linhaNaTabelaPercentualdeGordura,
		percentualDeGorduraMedido,
		peso: evaluation?.currentWeight,
		protocol: evaluation?.corporalEvaluationProtocol,
		proposedFatPercentage: evaluation?.proposedFatPercentage,
	};
}
